import { useId } from "react";
import { IBodyMetricEntry } from "../../models/bodyMetricModels";
import styles from "./BodyMetricCard.module.scss";

const WIDTH = 100;
const HEIGHT = 32;
const PADDING = 3;

/** Points of the line, oldest reading on the left; a flat line when nothing changed. */
export const toSparklinePoints = (history: IBodyMetricEntry[]): [number, number][] => {
  const values = history.map((e) => e.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const step = values.length > 1 ? WIDTH / (values.length - 1) : 0;
  return values.map((value, index) => [
    index * step,
    span === 0 ? HEIGHT / 2 : PADDING + (1 - (value - min) / span) * (HEIGHT - 2 * PADDING),
  ]);
};

interface IBodyMetricSparklineProps {
  history: IBodyMetricEntry[];
}

/** A small trend line under a metric card; drawn from two readings up. */
export const BodyMetricSparkline = ({ history }: IBodyMetricSparklineProps) => {
  // useId() has colons; keep the url(#…) reference plain.
  const gradientId = `metric-spark-${useId().replace(/:/g, "")}`;
  if (history.length < 2) return null;

  const points = toSparklinePoints(history);
  const line = points.map(([x, y]) => `${x},${y}`).join(" ");
  const area = `0,${HEIGHT} ${line} ${WIDTH},${HEIGHT}`;

  return (
    <svg
      className={styles.root_sparkline}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" className={styles.root_sparkline_stop_top} />
          <stop offset="100%" className={styles.root_sparkline_stop_bottom} />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gradientId})`} />
      <polyline
        points={line}
        fill="none"
        className={styles.root_sparkline_line}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};
